import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { LogoMark } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Calendar, ArrowRight, Tag, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
};

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/posts`).then((r) => setPosts(r.data)).finally(() => setLoading(false));
    document.title = "Blog & Guide · ToolBadge";
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="min-h-screen bg-[#FFFAF5]" data-testid="blog-list-page">
      <nav className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-[#EDE5D8]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" aria-label="Home">
            <LogoMark size={56} />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm hover:text-[#FF7A29]">← Home</Link>
            <Link to="/login"><Button variant="ghost" className="rounded-full">Accedi</Button></Link>
            <Link to="/register"><Button className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] text-white">Inizia</Button></Link>
          </div>
        </div>
      </nav>

      <header className="max-w-[1200px] mx-auto px-6 md:px-10 pt-16 pb-10">
        <div className="inline-flex items-center gap-2 bg-[#FF7A29]/10 text-[#E85D00] text-xs font-semibold uppercase tracking-[0.18em] px-4 py-2 rounded-full mb-5">
          <BookOpen className="w-3.5 h-3.5"/> Blog & Guide
        </div>
        <h1 className="font-display text-5xl md:text-6xl leading-tight">Idee per una gestione HR <span className="text-[#FF7A29]">più semplice</span>.</h1>
        <p className="text-[#555] mt-4 text-lg max-w-3xl">Articoli, guide e best practice su timbrature digitali, gestione ferie, compliance GDPR e tutto quello che riguarda il lavoro ibrido in Italia.</p>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 md:px-10 pb-20">
        {loading && <div className="text-center py-20 text-[#757575]">Caricamento articoli…</div>}

        {!loading && posts.length === 0 && (
          <div className="card-smart p-12 text-center" data-testid="blog-empty">
            <BookOpen className="w-12 h-12 mx-auto text-[#FF7A29]/50 mb-4"/>
            <h2 className="font-display text-2xl mb-2">Primi articoli in arrivo</h2>
            <p className="text-[#757575]">Stiamo preparando contenuti utili per voi. Torna a trovarci presto.</p>
          </div>
        )}

        {featured && (
          <Link to={`/blog/${featured.slug}`} data-testid={`blog-featured-${featured.slug}`} className="block mb-12 group">
            <article className="card-smart overflow-hidden border-0 hover:shadow-2xl hover:shadow-orange-200/50 transition-all">
              <div className="grid md:grid-cols-2 gap-0">
                {featured.cover_image_url ? (
                  <div className="aspect-[16/10] md:aspect-auto overflow-hidden bg-[#0A0A0A]">
                    <img src={featured.cover_image_url} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                  </div>
                ) : (
                  <div className="aspect-[16/10] md:aspect-auto bg-gradient-to-br from-[#FF7A29] to-[#E85D00] flex items-center justify-center">
                    <BookOpen className="w-20 h-20 text-white/80"/>
                  </div>
                )}
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#FF7A29] font-bold mb-3">
                    <span>In evidenza</span>
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl leading-tight mb-4 group-hover:text-[#FF7A29] transition-colors">{featured.title}</h2>
                  <p className="text-[#555] mb-6">{featured.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-[#757575]">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5"/> {fmtDate(featured.published_at)}</span>
                    <span>·</span>
                    <span>{featured.author}</span>
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 text-[#FF7A29] font-semibold text-sm group-hover:gap-3 transition-all">
                    Leggi l'articolo <ArrowRight className="w-4 h-4"/>
                  </div>
                </div>
              </div>
            </article>
          </Link>
        )}

        {rest.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-post-card-${p.slug}`} className="block group">
                <article className="card-smart overflow-hidden h-full flex flex-col hover:shadow-xl hover:shadow-orange-200/40 transition-all">
                  {p.cover_image_url ? (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={p.cover_image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                    </div>
                  ) : (
                    <div className="aspect-[16/10] bg-gradient-to-br from-[#0A0A0A] to-[#333] flex items-center justify-center">
                      <BookOpen className="w-12 h-12 text-white/60"/>
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-display text-xl leading-tight mb-2 group-hover:text-[#FF7A29] transition-colors">{p.title}</h3>
                    <p className="text-sm text-[#555] mb-4 flex-1">{p.excerpt}</p>
                    {p.tags && p.tags.length > 0 && (
                      <div className="flex gap-1.5 flex-wrap mb-3">
                        {p.tags.slice(0, 3).map((t) => (
                          <span key={t} className="text-[10px] uppercase tracking-widest text-[#757575] bg-[#FFF0E3] rounded-full px-2 py-0.5 font-semibold"><Tag className="w-2.5 h-2.5 inline mr-1"/>{t}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs text-[#757575] pt-3 border-t border-[#EDE5D8]">
                      <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3"/> {fmtDate(p.published_at)}</span>
                      <ArrowRight className="w-4 h-4 text-[#FF7A29] group-hover:translate-x-1 transition-transform"/>
                    </div>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        )}
      </main>

      <footer className="bg-[#0A0A0A] text-white/60 py-10 text-center text-sm">
        © {new Date().getFullYear()} ToolBadge · <Link to="/" className="hover:text-[#FF7A29]">Home</Link> · <Link to="/privacy" className="hover:text-[#FF7A29]">Privacy</Link> · <Link to="/terms" className="hover:text-[#FF7A29]">Termini</Link>
      </footer>
    </div>
  );
}
